/**
 * One-click confirmation for the 0.70–0.88 Dice-score band (PLAN §5): these
 * are plausible but not certain enough to auto-accept, so each gets its top
 * guess plus a couple of runner-ups to pick from instead of a free-text search.
 */

import type { Entry, EntryId } from "@schema/catalog.ts";
import type { MatchOutcome } from "@/lib/letterboxd/match.ts";
import type { LetterboxdFilmRecord } from "@/lib/letterboxd/rows.ts";
import styles from "./MatchReview.module.css";

export interface ReviewRow {
  index: number;
  record: LetterboxdFilmRecord;
  outcome: MatchOutcome;
}

interface Props {
  rows: ReviewRow[];
  entryById: Map<EntryId, Entry>;
  /** `undefined` = not yet resolved, `null` = explicitly "none of these", else the chosen entry. */
  resolutions: Record<number, EntryId | null>;
  onResolve: (index: number, entryId: EntryId | null) => void;
}

function formatPercent(score: number): string {
  return `${Math.round(score * 100)}%`;
}

export function MatchReview({ rows, entryById, resolutions, onResolve }: Props) {
  if (rows.length === 0) return null;

  return (
    <section className={styles.review} aria-label="Confirm uncertain matches">
      <h3 className={styles.heading}>
        Confirm {rows.length} uncertain {rows.length === 1 ? "match" : "matches"}
      </h3>
      <p className={styles.hint}>Pick the right film, or "None of these" to skip it.</p>
      <ul className={styles.list}>
        {rows.map(({ index, record, outcome }) => {
          const resolved = resolutions[index];
          const candidates = outcome.hit ? [outcome.hit, ...outcome.alternates] : [];
          return (
            <li key={index} className={styles.row}>
              <p className={styles.source}>
                {record.title}
                {record.year ? ` (${record.year})` : ""}
              </p>
              <div className={styles.choices}>
                {candidates.map((candidate) => {
                  const entry = entryById.get(candidate.entryId);
                  if (!entry) return null;
                  const active = resolved === candidate.entryId;
                  return (
                    <button
                      key={candidate.entryId}
                      type="button"
                      className={`${styles.choice} ${active ? styles.active : ""}`}
                      aria-pressed={active}
                      onClick={() => onResolve(index, candidate.entryId)}
                    >
                      {entry.title} ({entry.year}) · {formatPercent(candidate.score)}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className={`${styles.choice} ${resolved === null ? styles.active : ""}`}
                  aria-pressed={resolved === null}
                  onClick={() => onResolve(index, null)}
                >
                  None of these
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
